import { getConfigDir } from "@toolprint/sdk";
import { z } from "zod";
import { logger } from "../logger.js";

export interface SecretStorage<T> {
  exists(key: string): Promise<boolean>;
  get(key: string): Promise<T | undefined>;
  set(key: string, value: T): Promise<void>;
  delete(key: string): Promise<void>;
}

export const newSecretStorage = async <T>(
  schema: z.ZodSchema<T>,
  location: "config-dir" | "keyring" | "in-memory" = "in-memory",
): Promise<SecretStorage<T>> => {
  logger.info("Using secret storage for", location);
  if (location === "keyring") {
    return await newKeyringStorage<T>(schema);
  } else if (location === "config-dir") {
    return await newConfigDirStorage<T>(schema);
  }
  return await newInMemoryStorage<T>(schema);
};

async function newConfigDirStorage<T>(schema: z.ZodType<T, z.ZodTypeDef, T>) {
  const path = await import("path");
  const fs = await import("fs/promises");
  const configDir = await getConfigDir();

  class SchemaValidatedConfigDirSecretStorage implements SecretStorage<T> {
    constructor(private schema: z.ZodSchema<T>) {
      this.schema = schema;
    }

    private filePathForKey(key: string) {
      // Ensure the key is a valid filename by replacing any non-alphanumeric characters with underscores
      const sanitizedKey = key.replace(/[^a-zA-Z0-9]/g, "_");
      return path.join(configDir, sanitizedKey + ".json");
    }

    async exists(key: string): Promise<boolean> {
      const filePath = this.filePathForKey(key);
      logger.info("Checking if secret exists", filePath);
      return this.get(key).then((value) => value !== undefined);
    }

    async get(key: string): Promise<T | undefined> {
      logger.info("Getting secret", key);
      const filePath = this.filePathForKey(key);
      if (
        !(await fs
          .access(filePath)
          .then(() => true)
          .catch(() => false))
      ) {
        logger.info("Secret does not exist", filePath);
        return undefined;
      }

      logger.info("Reading secret from", filePath);
      const fileContent = await fs.readFile(filePath, "utf8");
      const validated = this.schema.safeParse(JSON.parse(fileContent));
      if (validated) {
        return validated.data;
      }
      return undefined;
    }

    async set(key: string, value: T): Promise<void> {
      logger.info("Setting secret", key);
      const filePath = this.filePathForKey(key);
      logger.info("Writing secret to", filePath);
      const jsonString = JSON.stringify(value, null, 2);
      await fs.writeFile(filePath, jsonString);
    }

    async delete(key: string): Promise<void> {
      logger.info("Deleting secret", key);
      const filePath = this.filePathForKey(key);
      logger.info("Deleting secret from", filePath);
      await fs.rm(filePath);
    }
  }

  return new SchemaValidatedConfigDirSecretStorage(schema);
}

async function newKeyringStorage<T>(schema: z.ZodType<T, z.ZodTypeDef, T>) {
  const { Entry } = await import("@napi-rs/keyring");

  class SchemaValidatedKeyringSecretStorage implements SecretStorage<T> {
    constructor(private schema: z.ZodSchema<T>) {
      this.schema = schema;
    }

    private entryForKey(key: string) {
      return new Entry("toolprint", key);
    }

    async exists(key: string): Promise<boolean> {
      const entry = this.entryForKey(key);
      return this.get(key).then((value) => value !== undefined);
    }

    async get(key: string): Promise<T | undefined> {
      logger.info("Getting secret", key);
      const entry = this.entryForKey(key);
      try {
        const value = await entry.getPassword();
        if (value) {
          const validated = this.schema.safeParse(JSON.parse(value));
          if (validated) {
            return validated.data;
          }
        }
      } catch (error) {
        logger.error("Error getting secret", key, error);
      }
      return undefined;
    }

    async set(key: string, value: T): Promise<void> {
      logger.info("Setting secret", key);
      const entry = this.entryForKey(key);
      const jsonString = JSON.stringify(value);
      entry.setPassword(jsonString);
    }

    async delete(key: string): Promise<void> {
      logger.info("Deleting secret", key);
      const entry = this.entryForKey(key);
      await entry.deletePassword();
    }
  }

  return new SchemaValidatedKeyringSecretStorage(schema);
}

async function newInMemoryStorage<T>(schema: z.ZodType<T, z.ZodTypeDef, T>) {
  class SchemaValidatedInMemorySecretStorage implements SecretStorage<T> {
    private storage: Map<string, T> = new Map();

    constructor(private schema: z.ZodSchema<T>) {
      this.schema = schema;
    }

    async exists(key: string): Promise<boolean> {
      logger.debug(`Checking if in-memory secret exists: ${key}`);
      return this.storage.has(key);
    }

    async get(key: string): Promise<T | undefined> {
      logger.debug(`Getting in-memory secret: ${key}`);
      const value = this.storage.get(key);

      if (value === undefined) {
        logger.debug(`In-memory secret not found: ${key}`);
        return undefined;
      }

      // Validate the stored value against the schema
      const validated = this.schema.safeParse(value);
      if (validated.success) {
        return validated.data;
      } else {
        logger.error(
          `Invalid in-memory secret format for key: ${key}`,
          validated.error,
        );
        return undefined;
      }
    }

    async set(key: string, value: T): Promise<void> {
      logger.debug(`Setting in-memory secret: ${key}`);

      // Validate the value before storing
      const validated = this.schema.safeParse(value);
      if (!validated.success) {
        throw new Error(
          `Invalid value for secret ${key}: ${validated.error.message}`,
        );
      }

      this.storage.set(key, validated.data);
    }

    async delete(key: string): Promise<void> {
      logger.debug(`Deleting in-memory secret: ${key}`);
      this.storage.delete(key);
    }

    /**
     * Clear all stored secrets. Useful for cleanup.
     */
    async clear(): Promise<void> {
      logger.debug("Clearing all in-memory secrets");
      this.storage.clear();
    }
  }

  return new SchemaValidatedInMemorySecretStorage(schema);
}
